import styled, { css } from "styled-components";
import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { useAppContext } from "../../../contexts/Context";
import { useAuth } from "../../../hooks/useAuth";
import { useDispatch } from "react-redux";

// ─────────────────────────────────────────────────────────────────
// SIDEBAR CONFIGURATION
// Items at the top level render as plain links.
// Items with a `children` array render as a collapsible group.
// ─────────────────────────────────────────────────────────────────
const sidebarConfig = [
  {
    id: 1,
    icon: "uil:create-dashboard",
    link: PATH_DASHBOARD.admin.index,
    title: "Dashboard",
  },
  // ── Students group ──────────────────────────────────────────────
  {
    id: "students-group",
    icon: "fluent:people-team-16-filled",
    title: "Students",
    children: [
      {
        id: 3,
        icon: "fluent:person-available-20-regular",
        link: PATH_DASHBOARD.admin.studentsList,
        title: "Active Students",
      },
      {
        id: 9,
        icon: "fluent-mdl2:deactivate-orders",
        link: PATH_DASHBOARD.admin.deactivated,
        title: "Deactivated Students",
      },
    ],
  },
  // ── Flat links ──────────────────────────────────────────────────
  {
    id: 2,
    icon: "mdi:google-classroom",
    link: PATH_DASHBOARD.admin.teachersList,
    title: "Teachers",
  },
  {
    id: 11,
    icon: "fluent:people-community-28-regular",
    link: PATH_DASHBOARD.admin.parentsList,
    title: "Parents",
  },
  // ── Results group ───────────────────────────────────────────────
  {
    id: "results-group",
    icon: "fluent-mdl2:poll-results",
    title: "Results",
    children: [
      {
        id: 4,
        icon: "fluent-mdl2:poll-results",
        link: PATH_DASHBOARD.admin.reports,
        title: "View Results",
      },
      {
        id: 17,
        icon: "fa6-solid:cloud-arrow-up",
        link: PATH_DASHBOARD.admin.uploadResults,
        title: "Upload Results",
      },
    ],
  },
  {
    id: 5,
    icon: "fluent:form-multiple-28-regular",
    link: PATH_DASHBOARD.admin.applications,
    title: "Applications",
  },
  {
    id: 7,
    icon: "oui:ml-create-population-job",
    link: PATH_DASHBOARD.admin.classesByData,
    title: "Population by Class",
  },
  {
    id: 8,
    icon: "fluent:form-multiple-28-regular",
    link: PATH_DASHBOARD.admin.classesAndSubjects,
    title: "Classes and Subjects",
  },
  {
    id: 6,
    icon: "solar:calendar-broken",
    link: PATH_DASHBOARD.admin.createTerm,
    title: "Term",
  },
  {
    id: 10,
    icon: "ion:folder-open-outline",
    link: PATH_DASHBOARD.admin.templates,
    title: "Templates Archive",
  },
  {
    id: 18,
    icon: "material-symbols:smart-display-outline",
    link: PATH_DASHBOARD.admin.marqueeSettings,
    title: "Marquee Settings",
  },
  // ── Bills & Fees group ──────────────────────────────────────────
  {
    id: "bills-group",
    icon: "material-symbols:account-balance-wallet-outline",
    title: "Bills & Fees",
    children: [
      {
        id: 12,
        icon: "material-symbols:account-balance-wallet-outline",
        link: PATH_DASHBOARD.admin.feesSetup,
        title: "Fees Setup",
      },
      {
        id: 13,
        icon: "fluent:receipt-money-24-regular",
        link: PATH_DASHBOARD.admin.generateBills,
        title: "Generate Bills",
      },
      {
        id: 14,
        icon: "icon-park-outline:add-user",
        link: PATH_DASHBOARD.admin.assignOtherFees,
        title: "Assign Custom Fees",
      },
      {
        id: 15,
        icon: "mdi:cash-register",
        link: PATH_DASHBOARD.admin.recordPayments,
        title: "Record Payments",
      },
      {
        id: 16,
        icon: "mdi:account-supervisor",
        link: PATH_DASHBOARD.admin.familyBills,
        title: "Family Bills",
      },
    ],
  },
];

export default function AdminSidebar() {
  const [activeTab, setActiveTab] = useState("Dashboard");
  // Track which groups are open by their id
  const [openGroups, setOpenGroups] = useState({
    "bills-group": false,
    "students-group": false,
    "results-group": false,
  });
  const { isSidebarOpen, setIsSidebarOpen, setIsProfileOpen } = useAppContext();
  const { logout } = useAuth();
  const location = useLocation();

  function handleNavClick(title) {
    setActiveTab(title);
    setIsSidebarOpen(false);
    setIsProfileOpen(false);
  }

  function toggleGroup(id) {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <SIDEBAR>
      <div
        className={`container d-flex flex-row ${isSidebarOpen ? "opened" : "closed"
          }`}
      >
        <div className="nav-container d-flex flex-column py-3 justify-content-between h-100 px-0">
          <div className="wrapper d-flex flex-column justify-content-between gap-3">
            <div className="bar-header w-100 d-flex justify-content-start">
              <p>Admin Console</p>
            </div>
            <div className="nav-links d-flex flex-column">
              {sidebarConfig.map((item) => {
                // ── Group item ──
                if (item.children) {
                  const isOpen = openGroups[item.id];
                  const isGroupActive = item.children.some(
                    (child) =>
                      location.pathname === child.link ||
                      activeTab === child.title,
                  );
                  return (
                    <div key={item.id}>
                      <GroupHeader
                        onClick={() => toggleGroup(item.id)}
                        isActive={isGroupActive}
                      >
                        <div className="child-link">
                          <Icon icon={item.icon} className="icon" />
                          <span>{item.title}</span>
                        </div>
                        <ChevronIcon
                          icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                          width="18"
                        />
                      </GroupHeader>

                      <GroupChildren isOpen={isOpen}>
                        {item.children.map((child) => (
                          <Link
                            key={child.id}
                            className={`nav-link react-router-link ${activeTab === child.title ||
                                location.pathname === child.link
                                ? "active-tab"
                                : ""
                              }`}
                            to={child.link}
                            onClick={() => handleNavClick(child.title)}
                          >
                            <div className="child-link child-link--nested">
                              <Icon icon={child.icon} className="icon" />
                              {child.title}
                            </div>
                          </Link>
                        ))}
                      </GroupChildren>
                    </div>
                  );
                }

                // ── Plain link ──
                return (
                  <Link
                    className={`nav-link react-router-link ${activeTab === item.title ? "active-tab" : ""
                      }`}
                    to={item.link}
                    key={item.id}
                    onClick={() => handleNavClick(item.title)}
                  >
                    <div className="child-link">
                      <Icon icon={item.icon} className="icon" />
                      {item.title}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="log-out nav-links d-flex flex-column">
            <Link
              className={`nav-link react-router-link ${activeTab === "Settings" ? "active-tab" : ""
                }`}
              to={PATH_DASHBOARD.admin.accountSettings}
              onClick={() => handleNavClick("Settings")}
            >
              {" "}
              <div className="child-link">
                <Icon icon="fluent:settings-20-regular" className="icon" />{" "}
                Settings
              </div>
            </Link>
            <button
              to=""
              className="react-router-link nav-link"
              onClick={logout}
            >
              <div className="child-link">
                <Icon
                  icon="streamline:interface-logout-arrow-exit-frame-leave-logout-rectangle-right"
                  rotate={2}
                  className="icon"
                />
                Log out
              </div>
            </button>
          </div>
        </div>
        <Closer
          className="d-flex"
          onClick={() => {
            setIsSidebarOpen(false);
          }}
        ></Closer>
      </div>
    </SIDEBAR>
  );
}

// ─────────────────────────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────────────────────────

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px 10px 0;
  cursor: pointer;
  border-left: 2px solid ${(p) => (p.isActive ? "white" : "black")};
  transition: 0.2s;

  .child-link {
    display: flex;
    font-weight: 300 !important;
    color: white !important;
    justify-content: left !important;
    align-items: center;
    padding-left: 15px;
    gap: 20px;
    font-size: 15px;

    .icon {
      height: 30px !important;
      width: 18px !important;
    }
  }

  &:hover {
    border-left: 2px solid white;
  }
`;

const ChevronIcon = styled(Icon)`
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
  margin-right: 4px;
  transition: transform 0.2s;
`;

const GroupChildren = styled.div`
  overflow: hidden;
  max-height: ${(p) => (p.isOpen ? "500px" : "0")};
  transition: max-height 0.3s ease-in-out;
  background: rgba(255, 255, 255, 0.05);
  border-left: 2px solid rgba(255, 255, 255, 0.2);
  margin-left: 0;

  .child-link--nested {
    padding-left: 30px !important;
    font-size: 14px !important;
  }
`;

const SIDEBAR = styled.div`
  background-color: black;
  height: 100vh;
  width: 20%;
  position: relative;
  z-index: 9999;
  .container {
    width: 20%;
    height: 100vh;
    align-items: center;
    position: fixed;
    background-color: black;
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .nav-container {
    width: 100%;
    overflow-y: auto;
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
    }
  }
  .wrapper {
    height: fit-content !important;
    width: 100%;
    align-items: center;
    padding-right: 0 !important;
  }
  .nav-links {
    width: 100% !important;
  }
  .nav-link {
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 15px;
    border-left: 2px solid black;

    .child-link {
      display: flex;
      font-weight: 300 !important;
      color: white !important;
      justify-content: left !important;
      align-items: center;
      padding-left: 15px;
      gap: 20px;
    }
    &:hover {
      color: white !important;
      transition: 0.3s;
      border-left: 2px solid white;
    }
    .icon {
      height: 30px !important;
      width: 18px !important;
    }
  }
  .bar-header {
    color: white;
    padding-left: 15px;
  }
  .active-tab {
    border-left: 2px solid white;
  }
  .logo {
    height: 80px;
    width: 80px;
    display: flex;
    border-radius: 50%;
    background-color: white;
    img {
      display: block;
      height: 100%;
      width: 100%;
      object-fit: contain;
      overflow: hidden;
      border-radius: 50%;
    }
  }
  .log-out {
    .nav-link {
      padding-top: 10px;
      padding-bottom: 10px;
      font-size: 15px;
      border-left: 2px solid black;
      .child-link {
        display: flex;
        font-weight: 300 !important;
        color: white !important;
        justify-content: left !important;
        align-items: center;
        padding-left: 15px;
        gap: 20px;
      }
      &:hover {
        color: white !important;
        transition: 0.3s;
        border-left: 2px solid white;
      }
      .icon {
        height: 30px !important;
        width: 18px !important;
      }
    }
  }
  @media screen and (max-width: 1100px) {
    width: 0 !important;
    .container {
      background: black;
    }
    .closed {
      margin-left: -1000px;
      transition: 0.3s;
    }
    .opened {
      width: 250px;
      margin-left: 0;
      transition: 0.3s;
      display: flex;
      position: fixed !important;
      z-index: 2;
    }
  }
  @media screen and (max-width: 550px) {
    .container {
      padding-right: 0 !important;
      padding-left: 0 !important;
      background-color: transparent;
    }
    .opened {
      width: 100%;
    }
    .nav-container {
      width: 250px;
      background-color: black;
    }
  }
`;

const Closer = styled.div`
  display: none;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  z-index: 9999;
  @media screen and (max-width: 500px) {
    display: flex;
    height: 100%;
    width: calc(100vw - 250px);
  }
`;
